/**
 * シーンのフレーム (静止画) 生成に添付する参照画像の「候補」を集める純粋関数。
 * docs/features/scene-frame-generation-tuning-plan.md §6.2。
 *
 * ここでは「何を候補にするか」だけを決める。どれを何枚どの順で添付するか (include_product の既定、
 * 上限、hero、重複統合、本文メンション) は BE のリゾルバ (frame_reference_plan.py) が決め、
 * 表示用のドライラン API と生成ジョブが同じ関数を使う。
 */
import type { GenerationAsset, CastingActor } from '../../state/types';
import type { SceneFrameReferenceOverrides } from '../../../../features/scenarios/types';

export type SceneFrameReferenceKind =
  | 'first_frame' | 'logo' | 'prev_frame' | 'mention' | 'added' | 'asset' | 'product' | 'casting';

export interface SceneFrameReferenceCandidate {
  /** 参照キー (§6.3): material:<id> / casting:<model_id> / prev_frame / logo / first_frame */
  key: string;
  kind: SceneFrameReferenceKind;
  material_id?: string | null;
  url?: string | null;
  label?: string;
  actor_id?: string;
}

export interface SceneFrameReferencePlanInput {
  candidates: SceneFrameReferenceCandidate[];
  overrides: SceneFrameReferenceOverrides | null;
  frame_type: 'first' | 'last';
}

/** 計画 (BE frame_reference_plan.py の戻り値) の要素 */
export interface SceneFrameReferencePlanItem {
  key: string;
  kind: SceneFrameReferenceKind;
  material_id: string | null;
  url: string | null;
  label: string;
  origins: { key: string; kind: SceneFrameReferenceKind; actor_id?: string }[];
  locked: boolean;
  readonly: boolean;
  default_on: boolean;
  status: 'attached' | 'excluded' | 'over_limit' | 'unresolved';
  reason: string | null;
  order: number | null;
  is_hero: boolean;
  actor_id?: string;
  reference_label?: string;
  token?: string | null;
  id4?: string;
}

export interface SceneFrameReferencePlan {
  frame_type: 'first' | 'last';
  scene_kind: 'normal' | 'endcard' | 'product_cut';
  limit: number;
  hero_key: string | null;
  items: SceneFrameReferencePlanItem[];
  attached: string[];
  casting_attached: string[];
  counts: { attached: number; limit: number; casting: number };
  scene_id?: string;
}

export interface BuildSceneFrameReferenceCandidatesInput {
  frameType: 'first' | 'last';
  scene: {
    preset_image_url?: string | null;
    preset_image_material_id?: string | null;
  };
  /** シナリオの選択中素材 (usage=reference のものだけ候補にする) */
  selectedAssets: ReadonlyArray<GenerationAsset>;
  /** このシーンで以前生成したフレームの素材 ID (候補から除く。+1 のときは空にする) */
  previousMaterialIds?: ReadonlySet<string>;
  /** 場面転換シーンの内部参照 (直前シーンの確定フレーム)。最初のフレーム生成のみ */
  internalReference?: { id: string; sourceUrl: string } | null;
  /** 最後のフレーム生成の基準になる現在の最初のフレーム */
  firstFrame?: { id: string | null; url: string } | null;
  /** キャスティング (登場人物A/B… 順)。character_sheet_public_url が無い俳優は候補に出さない */
  casting?: ReadonlyArray<CastingActor>;
  /** このシーンだけ追加した素材 (overrides.added を解決したもの。url が無ければ unresolved として一覧に残る) */
  addedMaterials?: ReadonlyArray<{ id: string; url: string | null; name?: string | null }>;
}

/** キャスティング素材の materials[].id (`casting-<model_id>`) から参照キーへ */
export function castingReferenceKey(actorId: string): string {
  return `casting:${actorId}`;
}

export function materialReferenceKey(materialId: string): string {
  return `material:${materialId}`;
}

/**
 * 候補を集める。並びは FE の都合 (ロゴ → 最初のフレーム → 内部参照 → 選択素材 → 追加 → キャスティング) で、
 * 添付順は BE が決め直す。
 */
export function buildSceneFrameReferenceCandidates(
  input: BuildSceneFrameReferenceCandidatesInput,
): SceneFrameReferenceCandidate[] {
  const out: SceneFrameReferenceCandidate[] = [];
  const { scene, frameType } = input;

  if (scene.preset_image_material_id && scene.preset_image_url) {
    out.push({
      key: 'logo', kind: 'logo', material_id: scene.preset_image_material_id, url: scene.preset_image_url, label: 'ロゴ',
    });
  }
  if (frameType === 'last' && input.firstFrame?.url) {
    out.push({
      key: 'first_frame', kind: 'first_frame', material_id: input.firstFrame.id, url: input.firstFrame.url, label: '最初のフレーム',
    });
  }
  if (frameType === 'first' && input.internalReference) {
    out.push({
      key: 'prev_frame', kind: 'prev_frame', material_id: input.internalReference.id, url: input.internalReference.sourceUrl,
      label: '前のシーンのフレーム',
    });
  }
  const previous = input.previousMaterialIds ?? new Set<string>();
  for (const asset of input.selectedAssets) {
    if ((asset.usage ?? 'reference') !== 'reference') continue;
    if (previous.has(asset.id)) continue;
    out.push({
      key: materialReferenceKey(asset.id),
      kind: asset.hasProductLinks ? 'product' : 'asset',
      material_id: asset.id,
      url: asset.sourceUrl ?? null,
      label: asset.name?.trim() || (asset.hasProductLinks ? '商品画像' : '選択素材'),
    });
  }
  for (const added of input.addedMaterials ?? []) {
    out.push({
      key: materialReferenceKey(added.id), kind: 'added', material_id: added.id, url: added.url,
      label: added.name?.trim() || '追加した素材',
    });
  }
  for (const actor of input.casting ?? []) {
    if (!actor.character_sheet_public_url) continue;
    out.push({
      key: castingReferenceKey(actor.id), kind: 'casting', material_id: null, url: actor.character_sheet_public_url,
      label: actor.display_name ? `${actor.display_name}のキャラクターシート` : 'キャラクターシート',
      actor_id: actor.id,
    });
  }
  return out;
}

/**
 * 計画の 1 行のチェックを切り替えたときの新しい overrides を返す (純粋関数)。
 * - 既定で添付される行 (default_on) を外す = 編集可能な由来の全キーを exclude に入れる (1 行 1 チェック。§6.3)
 * - 既定で外れる行 (include_product=false の商品画像) を入れる = include に入れる
 * 変更後に既定と同じになった指定は落とす。locked / readonly の行は変えない。
 */
export function toggleReferencePlanItem(
  overrides: SceneFrameReferenceOverrides | null,
  item: Pick<SceneFrameReferencePlanItem, 'origins' | 'default_on' | 'locked' | 'readonly' | 'status'>,
  on: boolean,
): SceneFrameReferenceOverrides | null {
  if (item.locked || item.readonly) return overrides;
  const keys = item.origins.map((o) => o.key);
  const exclude = new Set(overrides?.exclude ?? []);
  const include = new Set(overrides?.include ?? []);
  for (const k of keys) {
    exclude.delete(k);
    include.delete(k);
  }
  if (on && !item.default_on) {
    for (const k of keys) include.add(k);
  }
  if (!on && item.default_on) {
    for (const k of keys) exclude.add(k);
  }
  return normalizeReferenceOverrides({
    exclude: [...exclude],
    include: [...include],
    added: overrides?.added ?? [],
  });
}

/** このシーンだけの参照 (added) に素材を足す (重複は無視) */
export function addAddedMaterial(
  overrides: SceneFrameReferenceOverrides | null,
  materialId: string,
): SceneFrameReferenceOverrides | null {
  const added = overrides?.added ?? [];
  if (added.includes(materialId)) return overrides;
  return normalizeReferenceOverrides({ ...(overrides ?? {}), added: [...added, materialId] });
}

/** このシーンだけの参照 (added) から素材を外す。その素材への exclude / include 指定も一緒に落とす */
export function removeAddedMaterial(
  overrides: SceneFrameReferenceOverrides | null,
  materialId: string,
): SceneFrameReferenceOverrides | null {
  if (!overrides) return null;
  const key = materialReferenceKey(materialId);
  return normalizeReferenceOverrides({
    exclude: (overrides.exclude ?? []).filter((k) => k !== key),
    include: (overrides.include ?? []).filter((k) => k !== key),
    added: (overrides.added ?? []).filter((id) => id !== materialId),
  });
}

/** 空の配列を落とし、全部空なら null にする */
export function normalizeReferenceOverrides(
  overrides: SceneFrameReferenceOverrides | null | undefined,
): SceneFrameReferenceOverrides | null {
  if (!overrides) return null;
  const out: SceneFrameReferenceOverrides = {};
  if (overrides.exclude?.length) out.exclude = [...overrides.exclude];
  if (overrides.include?.length) out.include = [...overrides.include];
  if (overrides.added?.length) out.added = [...overrides.added];
  return Object.keys(out).length > 0 ? out : null;
}

/** 計画の行の表示用の状態文言 */
export function describeReferencePlanStatus(item: SceneFrameReferencePlanItem): string | null {
  switch (item.status) {
    case 'over_limit':
      return '上限で未添付';
    case 'unresolved':
      return item.reason === 'material_unavailable' ? '参照先の素材を取得できません' : '素材が見つかりません';
    case 'excluded':
      return item.reason === 'endcard' ? 'エンドカードはロゴのみ' : item.reason === 'default_off' ? '商品を含めないシーンのため既定で外れています' : null;
    default:
      return null;
  }
}
